import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, FileText, Lock, Zap } from "lucide-react";
import LedgerRow from "./LedgerRow";

/**
 * EnrollSidebar — sticky receipt-style summary that mirrors the customer's
 * selected plan and address. Always reflects the latest props from parent;
 * the only internal state is the documents collapsed/expanded toggle.
 */
export default function EnrollSidebar({
  currentPlan,
  estimate,
  address,
  unit,
  city,
  zip,
  esiidParam,
  utilityParam,
}) {
  const [showDocs, setShowDocs] = useState(false);

  return (
    <aside className="min-w-0 lg:sticky lg:top-20 h-fit" data-testid="enroll-summary">
      <div className="relative rounded-[16px] bg-white border border-slate-200 shadow-[0_24px_60px_-32px_rgba(15,29,61,0.18)] overflow-hidden">
        {/* Left accent bar — corporate identity strip */}
        <div
          className="absolute top-0 left-0 bottom-0 w-1 bg-gradient-to-b from-velcor-green to-[#256330]"
          aria-hidden
        />

        {/* Header with brand + plan */}
        <div className="px-5 md:px-7 pt-5 md:pt-7 pb-4 md:pb-5 border-b border-slate-100">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-velcor-green">
              Tu plan seleccionado
            </div>
            <span className="grid place-items-center w-6 h-6 md:w-7 md:h-7 rounded-lg bg-velcor-green/10 text-velcor-green">
              <Zap className="w-3 h-3 md:w-3.5 md:h-3.5" strokeWidth={2.6} />
            </span>
          </div>
          <div
            className="text-velcor-ink leading-[1.1] mb-1 text-[20px] md:text-[24px]"
            style={{ fontWeight: 800, letterSpacing: "-0.022em" }}
            data-testid="summary-plan-name"
          >
            {currentPlan.name}
          </div>
          <div className="text-[10.5px] md:text-[11px] font-bold text-slate-500 uppercase tracking-tight">
            {currentPlan.term}
          </div>
        </div>

        {/* Line items — ledger style */}
        <dl className="px-5 md:px-7 pt-3 md:pt-4 pb-2 md:pb-3 divide-y divide-dashed divide-slate-200/70">
          <LedgerRow
            label="Dirección"
            value={
              address
                ? `${address}${unit ? " #" + unit : ""}${city ? ", " + city : ""}, TX${
                    zip ? " " + zip : ""
                  }`
                : `ZIP ${zip || "—"}`
            }
          />
          {esiidParam && (
            <LedgerRow
              label="ESIID"
              value={<span className="font-mono text-[11px] tracking-tight">{esiidParam}</span>}
            />
          )}
          <LedgerRow label="Compañía local" value={utilityParam || estimate?.utility_name || "—"} />
          <LedgerRow
            label="Consumo anual estimado"
            value={estimate?.annual_kwh ? `${estimate.annual_kwh.toLocaleString()} kWh` : "—"}
          />
          <LedgerRow
            label="Plazo"
            value={currentPlan.term?.split("·")[0]?.trim() || currentPlan.term}
          />
          <LedgerRow
            label="Tarifa fija"
            value={<span className="tabular-nums">{currentPlan.rate}¢ / kWh</span>}
          />
        </dl>

        {/* Invoice-style total — corporate receipt frame */}
        <div className="mx-5 md:mx-7 mb-4 md:mb-5 mt-2 rounded-xl border-2 border-velcor-green/20 bg-velcor-green/[0.04] px-4 py-3">
          <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-velcor-green mb-1.5 md:mb-2">
            Factura mensual estimada
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <div
              className="text-velcor-ink leading-none tabular-nums text-[30px] md:text-[38px]"
              style={{ fontWeight: 900, letterSpacing: "-0.028em" }}
              data-testid="summary-price"
            >
              {estimate?.annual_kwh
                ? `$${Math.max(
                    0,
                    Math.round((currentPlan.rate / 100) * (estimate.annual_kwh / 12)),
                  )}`
                : "$—"}
            </div>
            <div className="text-right">
              <div className="text-[10.5px] md:text-[11px] font-extrabold text-slate-600 tabular-nums uppercase tracking-tight">
                USD / mes
              </div>
              <div className="text-[9.5px] md:text-[10px] text-slate-400 mt-0.5 tabular-nums">
                @{" "}
                {(estimate?.annual_kwh
                  ? estimate.annual_kwh / 12
                  : currentPlan.kwh || 2000
                ).toLocaleString()}{" "}
                kWh/mes
              </div>
            </div>
          </div>
        </div>

        {/* Documents block — plan-specific EFL/Terms/YRAC (collapsible) */}
        <div className="border-t border-slate-100" data-testid="summary-documents">
          <button
            type="button"
            onClick={() => setShowDocs((v) => !v)}
            aria-expanded={showDocs}
            className="w-full px-5 md:px-7 py-3 md:py-3.5 flex items-center justify-between gap-2 hover:bg-slate-50/60 transition-colors"
            data-testid="summary-docs-toggle"
          >
            <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-slate-500">
              Documentos del plan
            </span>
            <ChevronDown
              className={`w-4 h-4 text-slate-400 transition-transform ${
                showDocs ? "rotate-180" : ""
              }`}
              strokeWidth={2.4}
            />
          </button>
          <AnimatePresence initial={false}>
            {showDocs && (
              <motion.ul
                key="docs-list"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden px-5 md:px-7 pb-3"
              >
                {[
                  {
                    href: `/electricity-facts-label/${currentPlan.id}`,
                    label: "Electricity Facts Label (EFL)",
                  },
                  { href: "/terms", label: "Términos del servicio" },
                  { href: "/yrac", label: "Derechos del cliente (YRAC)" },
                  { href: "/privacy", label: "Política de privacidad" },
                ].map((d) => (
                  <li key={d.href}>
                    <a
                      href={d.href}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-center justify-between gap-2 text-[12px] font-bold text-slate-600 hover:text-velcor-green py-1.5 transition-colors"
                      data-testid={`summary-doc-${d.href.split("/").pop()}`}
                    >
                      <span className="inline-flex items-center gap-2 min-w-0">
                        <FileText
                          className="w-3.5 h-3.5 text-slate-400 group-hover:text-velcor-green shrink-0"
                          strokeWidth={2.2}
                        />
                        <span className="truncate">{d.label}</span>
                      </span>
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </a>
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>

        {/* Privacy footer */}
        <div className="px-5 md:px-7 py-3 border-t border-slate-100 text-center bg-slate-50/40">
          <p className="text-[10px] md:text-[10.5px] text-slate-500 leading-snug inline-flex items-center justify-center gap-1.5 font-semibold">
            <Lock className="w-3 h-3 text-velcor-green" strokeWidth={2.4} />
            No compartimos tu información — conexión SSL cifrada
          </p>
        </div>
      </div>
    </aside>
  );
}
